import React, { useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { TooltipButton } from "components/common/TooltipButton";
import { InlineError } from "components/common/InlineError";
import { useMintOrgName } from "components/main/connections/useMintOrg";
import { SavedConnectionEditor } from "components/main/utils/settings/SavedConnectionEditor";
import { SupabaseConnectionEditor } from "components/main/utils/settings/SupabaseConnectionEditor";
import { BucketConnectionEditor } from "components/main/utils/settings/BucketConnectionEditor";
import { LocalSqliteEditor } from "components/main/utils/settings/LocalSqliteEditor";
import { isLocalSqliteSupported } from "components/canvas/leftSide/import/LocalSqlite";
import { connectionTypeLabel } from "./ConnectionTypeIcon";
import { ConnectionTypeTile } from "./ConnectionTypeTile";
import { useAddGoogleConnection } from "./useGoogleOauthPopup";
import type { ConnectionType } from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";

const SQLITE_UNSUPPORTED_DESCRIBED_BY_ID =
  "new-connection-sqlite-unsupported-help";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fires after a new connection is successfully created (the store
   *  has already added it to the cached list at this point). */
  onCreated: () => void;
}

interface TypeTile {
  type: ConnectionType;
  /** When false, the tile renders as "Coming soon" and is not selectable. */
  available: boolean;
  /** Single-line description shown under the tile name. */
  description: string;
}

const TYPE_TILES: TypeTile[] = [
  {
    type: "postgres",
    available: true,
    description:
      "RDS, self-hosted, Heroku, anything that speaks the Postgres protocol.",
  },
  {
    type: "supabase",
    available: true,
    description:
      "A Supabase project. Use the session pooler, transaction pooler, or direct connection string.",
  },
  {
    type: "google_oauth",
    available: true,
    description:
      "Google account for Sheets imports. Authorize via OAuth popup.",
  },
  {
    type: "bucket",
    available: true,
    description:
      "Google Cloud Storage bucket. One-shot import of CSV / TSV / Parquet objects as editable tables.",
  },
  {
    // D3: SQLite as the 5th tile (append). The per-tile capability gate
    // (D5) is applied at render time from `isLocalSqliteSupported()`, not
    // baked into `available` here.
    type: "local_sqlite",
    available: true,
    description: "Open a .sqlite / .db file on this device. Stays on disk.",
  },
];

const SQLITE_UNSUPPORTED_TOOLTIP =
  "Local SQLite needs Chrome, Edge, or Arc. You can still upload the file as CSV or Parquet.";

export const NewConnectionPanel: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [selectedType, setSelectedType] = useState<ConnectionType | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const { start: startGoogle, pending: googlePending } =
    useAddGoogleConnection();

  // Stay mounted through the close animation; `entered` drives the transform.
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    const t = setTimeout(() => setMounted(false), 400);
    return () => clearTimeout(t);
  }, [open]);

  // Reset only after unmount — resetting on `open=false` would flicker mid-slide-out.
  useEffect(() => {
    if (!mounted) {
      setSelectedType(null);
      setGoogleError(null);
    }
  }, [mounted]);

  const handlePick = async (type: ConnectionType) => {
    setGoogleError(null);
    if (type === "google_oauth") {
      const outcome = await startGoogle();
      if (outcome.status === "ok") {
        onCreated();
        onClose();
      } else if (outcome.status === "popup_blocked") {
        setGoogleError(
          "Allow popups for this site, then click Google again to authorize.",
        );
      } else if (outcome.status === "cancelled") {
        // Silent — popup closed without completing.
      } else if (outcome.status === "email_mismatch") {
        setGoogleError(
          `Already connected as ${outcome.expected}. To switch accounts, delete that connection and try again.`,
        );
      } else if (outcome.status === "error") {
        setGoogleError(outcome.message);
      }
      return;
    }
    setSelectedType(type);
  };

  if (!mounted) return null;

  const transition = "var(--duration-medium) var(--ease-enter)";

  return (
    <>
      {/* Same scrim as `RowOverlay` / `Dialog`. `bg-inverse-bg` flipped with the
          theme, washing the page white in dark mode. */}
      <div
        className="fixed inset-0 bg-black/20 z-50"
        style={{
          opacity: entered ? 1 : 0,
          transition: `opacity ${transition}`,
        }}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 w-full max-w-105 bg-bg border-l border-border z-50 flex flex-col shadow-2xl"
        style={{
          transform: entered ? "translateX(0)" : "translateX(100%)",
          transition: `transform ${transition}`,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-connection-title"
      >
        <header className="flex items-center justify-between h-12 px-5">
          <div className="flex items-center gap-2 min-w-0">
            {selectedType && (
              <TooltipButton
                title="Back"
                variant="default"
                size="xs"
                onClick={() => {
                  setSelectedType(null);
                  setGoogleError(null);
                }}
                aria-label="Back"
              >
                <ArrowBackIcon sx={{ fontSize: 14 }} />
              </TooltipButton>
            )}
            <h2 id="new-connection-title" className="text-fg-bold truncate">
              {selectedType === null
                ? "New connection"
                : selectedType === "bucket"
                  ? `New ${connectionTypeLabel(selectedType)}`
                  : `New ${connectionTypeLabel(selectedType)} connection`}
            </h2>
          </div>
          <TooltipButton
            title="Close"
            variant="default"
            size="xs"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </TooltipButton>
        </header>

        <div className="flex-1 overflow-auto p-5">
          {selectedType === null ? (
            <TypePickerStep
              onPick={handlePick}
              googlePending={googlePending}
              googleError={googleError}
            />
          ) : selectedType === "bucket" ? (
            <BucketConnectionEditor
              onSaved={() => {
                onCreated();
                onClose();
              }}
              onCancel={onClose}
              hideCancel
            />
          ) : selectedType === "local_sqlite" ? (
            <LocalSqliteEditor
              onSaved={() => {
                onCreated();
                onClose();
              }}
              onCancel={onClose}
              hideCancel
            />
          ) : selectedType === "supabase" ? (
            <SupabaseConnectionEditor
              onSaved={() => {
                onCreated();
                onClose();
              }}
              onCancel={onClose}
              hideCancel
            />
          ) : (
            <SavedConnectionEditor
              newConnectionType={selectedType}
              onSaved={() => {
                onCreated();
                onClose();
              }}
              onCancel={onClose}
              hideCancel
            />
          )}
        </div>
      </aside>
    </>
  );
};

const TypePickerStep: React.FC<{
  onPick: (t: ConnectionType) => void;
  googlePending: boolean;
  googleError: string | null;
}> = ({ onPick, googlePending, googleError }) => {
  // Cache the capability probe for this render. `isLocalSqliteSupported`
  // is cheap (synchronous feature-detect) but stable across the panel's
  // lifetime — recomputing per-tile would just be noise.
  const sqliteSupported = useMemo(() => isLocalSqliteSupported(), []);
  // D24: a credential minted into an invisible scope is only discovered when it
  // breaks. Omitted rather than guessed when the org cannot be resolved.
  const mintOrgName = useMintOrgName();
  return (
    <>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {TYPE_TILES.map((tile) => {
          const isSqliteBlocked =
            tile.type === "local_sqlite" && !sqliteSupported;
          const disabled =
            !tile.available ||
            (tile.type === "google_oauth" && googlePending) ||
            isSqliteBlocked;
          return (
            <ConnectionTypeTile
              key={tile.type}
              type={tile.type}
              description={tile.description}
              disabled={disabled}
              pillLabel={
                isSqliteBlocked ? "Requires Chrome / Edge / Arc" : undefined
              }
              describedBy={
                isSqliteBlocked ? SQLITE_UNSUPPORTED_DESCRIBED_BY_ID : undefined
              }
              description2={
                isSqliteBlocked ? SQLITE_UNSUPPORTED_TOOLTIP : undefined
              }
              onClick={() => onPick(tile.type)}
            />
          );
        })}
      </div>
      {googleError && <InlineError className="mb-2">{googleError}</InlineError>}
      {mintOrgName && (
        <p className="text-fg-muted-regular-small mb-1">
          Adding to <strong>{mintOrgName}</strong>. Only you can see it.
        </p>
      )}
      <p className="text-fg-muted-regular-small">
        More types (MySQL, BigQuery) coming soon.
      </p>
      {/* Visually hidden full tooltip text — referenced via
          aria-describedby on the disabled SQLite tile so keyboard /
          screen-reader users get the same path-forward copy hover users
          see in the title tooltip. */}
      <span id={SQLITE_UNSUPPORTED_DESCRIBED_BY_ID} className="sr-only">
        {SQLITE_UNSUPPORTED_TOOLTIP}
      </span>
    </>
  );
};
